import { Fragment, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/useAuth';
import { useEntitlementModules } from '@/features/admin/platformHooks';
import { filterMenuByModules, getMenuForRole, type MenuSection } from '../menuConfig';
import { SvgIcon } from '../SvgIcon';
import { SidebarChevron } from './SidebarChevron';

interface SidebarMenuItemsProps {
  onNavigate: () => void;
}

/** CUBA SidebarMenuItems — RCATS menus with stroke/fill SvgIcon */
export function SidebarMenuItems({ onNavigate }: SidebarMenuItemsProps) {
  const { role, user } = useAuth();
  const location = useLocation();
  const { modules } = useEntitlementModules(role, user?.organizationId);
  const menu = useMemo(
    () => filterMenuByModules(getMenuForRole(role), modules),
    [role, modules],
  );
  const [openTitles, setOpenTitles] = useState<Record<string, boolean>>({});

  const normalize = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);
  const currentPath = normalize(location.pathname);

  /** Exact match for portal roots (Dashboard/Overview/Console) so they don't stay active on child pages. */
  const pathExact = (path?: string) => Boolean(path && currentPath === normalize(path));

  /** Prefix match for nested submenu routes (e.g. /admin/students/:id). */
  const pathNested = (path?: string) => {
    if (!path) return false;
    const base = normalize(path);
    return currentPath === base || currentPath.startsWith(`${base}/`);
  };

  /** Leaf links: exact for portal roots, nested for module list/detail routes. */
  const linkActive = (path?: string) => {
    if (!path) return false;
    const base = normalize(path);
    if (base === '/admin' || base === '/') return pathExact(path);
    return pathNested(path);
  };

  const sectionOpen = (section: MenuSection) => {
    if (section.type === 'link') {
      return linkActive(section.path);
    }
    return (
      openTitles[section.title] ||
      Boolean(section.children?.some((c) => pathNested(c.path)))
    );
  };

  const childActive = (path: string, siblings: { path: string }[]) => {
    if (!pathNested(path)) return false;
    // Prefer the most specific sibling match so list vs create routes don't both look active.
    const longerMatch = siblings.some(
      (s) =>
        s.path !== path &&
        pathNested(s.path) &&
        normalize(s.path).length > normalize(path).length,
    );
    return !longerMatch;
  };

  const toggle = (title: string) => {
    // Accordion behavior: opening one level closes the others.
    setOpenTitles((prev) => {
      const currentlyOpen = Boolean(prev[title]);
      if (currentlyOpen) return {};
      return { [title]: true };
    });
  };

  const closeAfterNavigate = () => {
    // Clear local open state so the "open" styling comes from the route (pathActive),
    // not from a previously toggled section that no longer matches.
    setOpenTitles({});
    if (window.innerWidth <= 991) {
      document.querySelector('.page-header')?.classList.add('close_icon');
      document.querySelector('.sidebar-wrapper')?.classList.add('close_icon');
      onNavigate();
    }
  };

  return (
    <>
      {menu.map((group) => (
        <Fragment key={group.menutitle}>
          <li className="sidebar-main-title">
            <div>
              <h6 className="lan-1">{group.menutitle}</h6>
            </div>
          </li>
          {group.Items.map((menuItem) => {
            const open = sectionOpen(menuItem);
            return (
              <li
                className={`sidebar-list credora-nav-section ${open ? 'active credora-nav-section--open' : ''}`}
                key={menuItem.title}
              >
                {menuItem.type === 'sub' ? (
                  <a
                    href="#javascript"
                    className={`sidebar-link sidebar-title ${open ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      toggle(menuItem.title);
                    }}
                  >
                    <SvgIcon className="stroke-icon" iconId={`stroke-${menuItem.icon}`} />
                    <SvgIcon className="fill-icon" iconId={`fill-${menuItem.icon}`} />
                    <span>{menuItem.title}</span>
                    <SidebarChevron open={open} />
                  </a>
                ) : null}

                {menuItem.type === 'link' && menuItem.path ? (
                  <Link
                    to={menuItem.path}
                    className={`sidebar-link sidebar-title link-nav ${linkActive(menuItem.path) ? 'active' : ''}`}
                    onClick={closeAfterNavigate}
                  >
                    <SvgIcon className="stroke-icon" iconId={`stroke-${menuItem.icon}`} />
                    <SvgIcon className="fill-icon" iconId={`fill-${menuItem.icon}`} />
                    <span>{menuItem.title}</span>
                  </Link>
                ) : null}

                {menuItem.children ? (
                  <ul
                    className="sidebar-submenu credora-nav-submenu"
                    style={{ display: open ? 'block' : 'none' }}
                  >
                    {menuItem.children.map((child) => (
                      <li key={child.path}>
                        <Link
                          to={child.path}
                          className={childActive(child.path, menuItem.children ?? []) ? 'active' : ''}
                          onClick={closeAfterNavigate}
                        >
                          {child.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}
